import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tooltip, Typography } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { ArrowDownOutlined, EditOutlined } from '@ant-design/icons';
import { dbCols, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { DownwardArrow } from '../../schema/be-graphql-generated';
import { colors } from '../../styles/colors';
import { useContext, useEffect } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import useStyles from './styles.style';

const { Title } = Typography;

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const DownwardArrowsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const { styles } = useStyles();

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, setFilters } = useTable<DownwardArrow>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'downwardArrows',
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      operation: 'downwardArrows',
      fields: [
        {
          data: ['_id', 'userId', 'createdAt', 'updatedAt', 'beliefs'],
        },
        'count',
      ],
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  useActivityTracking(dbCols.downwardArrows, fixedClientId, !fixedClientId);

  const expandedRowRender = (record: DownwardArrow) => {
    const beliefs = (record?.beliefs || []).filter((b) => !!b);
    return (
      <>
        {beliefs.map((b, index) => {
          return (
            <div className={styles['downward-arrows--thoughts-container']}>
              <div key={index} id={`field$-${index}`}>
                {b}
              </div>

              {beliefs?.length > 1 && index < beliefs?.length - 1 && (
                <div className={styles['downward-arrows--question-container']}>
                  <ArrowDownOutlined
                    className={styles['downward-arrows--arrow-icon']}
                    color={colors['--triad-blue-0']}
                  />
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('shared.downwardArrows.downwardArrows')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-downward-arrow-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-downward-arrow-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/downwardArrows/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowRender,
        }}
        data-testid="downward-arrows-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: DownwardArrow) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}

        <Table.Column<DownwardArrow>
          key="beleifs"
          dataIndex="beleifs"
          title={translate('shared.downwardArrows.initialThought')}
          render={(text, record, indx) => {
            return record?.beliefs?.[0] || '';
          }}
        />

        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />

        <Table.Column<DownwardArrow>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            const client = clients.find((c) => c._id === record.userId);

            const hasPremiumAccess = client?.accessLevel;

            const canEdit =
              client?.access?.allowEdit ||
              (client?.access?.allowAdminEdit &&
                identity?.db?.role === UserRole.THERAPIST_ADMIN);

            return (
              <Space>
                {canEdit && (
                  <Tooltip
                    title={translate(
                      hasPremiumAccess
                        ? 'resourcesCommon.edit'
                        : 'clients.thoughtsTestDisabledTip',
                    )}
                  >
                    <Link
                      to={`/tools/downwardArrows/create?downwardArrowId=${record._id}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!hasPremiumAccess || !canEdit}
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
